import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { CaptchaConstants, CaptchaTypeSpecification, FunCaptcha } from 'Roblox';
import InlineChallenge from '../../../common/inlineChallenge';
import InlineChallengeBody from '../../../common/inlineChallengeBody';
import { InlineChallengeFooter } from '../../../common/inlineChallengeFooter';
import { FooterButtonConfig, FragmentModalFooter } from '../../../common/modalFooter';
import useCaptchaContext from '../hooks/useCaptchaContext';
import { ActionType, ErrorCode } from '../interface';
import { CaptchaReducerActionType } from '../store/action';

let CAPTCHA_ELEMENT_COUNT = 0;
// eslint-disable-next-line no-plusplus
const getNewCaptchaElementId = () => `captcha-element-${CAPTCHA_ELEMENT_COUNT++}`;

/**
 * A container element for the Captcha UI.
 */
const Captcha: React.FC = () => {
  const {
    state: {
      actionType,
      appType,
      dataExchangeBlob,
      unifiedCaptchaId,
      renderInline,
      resources,
      requestService,
      onChallengeDisplayed,
      onModalChallengeAbandoned,
      isModalVisible
    },
    dispatch
  } = useCaptchaContext();

  /*
   * Component State
   */

  const [captchaElementId] = useState<string>(getNewCaptchaElementId());
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [pageLoadError, setPageLoadError] = useState<string | null>(null);

  /*
   * Event Handlers
   */

  const closeModal = () => {
    dispatch({
      type: CaptchaReducerActionType.HIDE_MODAL_CHALLENGE
    });
    if (onModalChallengeAbandoned !== null) {
      onModalChallengeAbandoned(() =>
        dispatch({
          type: CaptchaReducerActionType.SHOW_MODAL_CHALLENGE
        })
      );
    }
  };

  /*
   * Effects
   */

  const loadChallenge = async () => {
    setPageLoading(true);
    setPageLoadError(null);

    const result = await requestService.captcha.getMetadata();
    if (result.isError) {
      setPageLoadError(`${resources.Message.Error.Default} ${resources.Action.PleaseTryAgain}`);
      setPageLoading(false);
      return;
    }

    // Compute an array of captcha type specs from metadata.
    const { funCaptchaPublicKeys } = result.value;
    const captchaTypes: CaptchaTypeSpecification[] = [];
    Object.keys(CaptchaConstants.funCaptchaPublicKeyMap).forEach(actionTypeRaw => {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const actionType = actionTypeRaw as ActionType;
      const publicKeyName = CaptchaConstants.funCaptchaPublicKeyMap[actionType];
      const publicKey = funCaptchaPublicKeys[publicKeyName];
      if (publicKey !== undefined) {
        captchaTypes.push({
          Type: actionType,
          PublicKey: publicKey
        });
      }
    });

    // Add the captcha type specs to the captcha service.
    FunCaptcha.addCaptchaTypes(captchaTypes, false);

    // Enable app type logging if an app type was provided.
    if (appType !== null) {
      FunCaptcha.setPerAppTypeLoggingEnabled(true, appType);
    }

    // Render the captcha.
    FunCaptcha.render(captchaElementId, {
      cType: actionType,
      inputParams: {
        dataExchange: dataExchangeBlob,
        unifiedCaptchaId
      },
      returnTokenInSuccessCb: true,
      shownCb: () => {
        setPageLoading(false);
        onChallengeDisplayed({ displayed: true });
      },
      errorCb: () =>
        dispatch({
          type: CaptchaReducerActionType.SET_CHALLENGE_INVALIDATED,
          errorCode: ErrorCode.UNKNOWN
        }),
      successCb: (captchaToken: string, captchaId: string) =>
        dispatch({
          type: CaptchaReducerActionType.SET_CHALLENGE_COMPLETED,
          onChallengeCompletedData: { captchaToken, captchaId }
        })
    });
  };

  // Challenge loading effect:
  useEffect(() => {
    // eslint-disable-next-line no-void
    void loadChallenge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Render Properties
   */

  const reloadButton: FooterButtonConfig = {
    content: resources.Action.Reload,
    label: resources.Action.Reload,
    enabled: pageLoadError !== null,
    action: loadChallenge
  };

  /*
   * Component Markup
   */

  return renderInline ? (
    <InlineChallenge titleText={resources.Header.RobotChallenge}>
      <InlineChallengeBody>
        {pageLoadError !== null && (
          <p className='challenge-captcha-body challenge-captcha-error-body'>{pageLoadError}</p>
        )}
        {pageLoading && (
          <span className='spinner spinner-default spinner-no-margin challenge-captcha-body' />
        )}
        {pageLoadError === null && <div className='challenge-captcha-body' id={captchaElementId} />}
      </InlineChallengeBody>
      {pageLoadError !== null && (
        <InlineChallengeFooter positiveButton={reloadButton} negativeButton={null} />
      )}
    </InlineChallenge>
  ) : (
    <Modal
      className='modal-modern modal-modern-challenge-captcha'
      show={isModalVisible}
      onHide={closeModal}
      backdrop='static'>
      <Modal.Body>
        <button
          type='button'
          className='challenge-captcha-close-button'
          onClick={closeModal}
          disabled={false}>
          <span className='icon-close' />
        </button>
        {pageLoadError !== null && (
          <p className='challenge-captcha-body challenge-captcha-error-body'>{pageLoadError}</p>
        )}
        {pageLoading && (
          <span className='spinner spinner-default spinner-no-margin challenge-captcha-body' />
        )}
        {pageLoadError === null && (
          // Don't add the body margins to the captcha element until it is actually rendered.
          <div className={pageLoading ? '' : 'challenge-captcha-body'} id={captchaElementId} />
        )}
      </Modal.Body>
      {pageLoadError !== null && (
        <FragmentModalFooter positiveButton={reloadButton} negativeButton={null} />
      )}
    </Modal>
  );
};

export default Captcha;
